export const CHAT_LIST_EMAIL_FOLDER_MOVE_ERROR_KEY = 'CONVERSATION.MOVE.ERROR';
export const CHAT_LIST_EMAIL_FOLDER_MOVE_SUCCESS_KEY =
  'CONVERSATION.MOVE.SUCCESS';
export const CHAT_LIST_EMAIL_FOLDER_ASSIGNMENT_ERROR_KEY =
  'CONVERSATION.GMAIL_LABEL_ASSIGNMENT.FAILED';
export const CHAT_LIST_EMAIL_FOLDER_ASSIGNMENT_SUCCESS_KEY =
  'CONVERSATION.GMAIL_LABEL_ASSIGNMENT.SUCCESS';
export const CHAT_LIST_STATUS_CHANGE_ERROR_KEY =
  'CONVERSATION.CHANGE_STATUS_FAILED';
export const CHAT_LIST_STATUS_CHANGE_SUCCESS_KEY = 'CONVERSATION.CHANGE_STATUS';
export const CHAT_LIST_MARK_AS_UNREAD_ERROR_KEY =
  'CONVERSATION.MARK_AS_UNREAD.FAILED';
export const CHAT_LIST_MARK_AS_UNREAD_SUCCESS_KEY =
  'CONVERSATION.MARK_AS_UNREAD.SUCCESS';
export const CHAT_LIST_PRIORITY_CHANGE_SUCCESS_KEY =
  'CONVERSATION.PRIORITY.CHANGE_PRIORITY.SUCCESSFUL';
export const CHAT_LIST_ASSIGN_AGENT_ERROR_KEY = 'BULK_ACTION.ASSIGN_FAILED';
export const CHAT_LIST_ASSIGN_LABELS_ERROR_KEY =
  'BULK_ACTION.LABELS.ASSIGN_FAILED';
export const CHAT_LIST_BULK_UPDATE_ERROR_KEY =
  'BULK_ACTION.UPDATE.UPDATE_FAILED';

export const CHAT_LIST_PROVIDER_GMAIL = 'gmail';
export const CHAT_LIST_PROVIDER_MICROSOFT = 'microsoft';
export const CHAT_LIST_PROVIDER_IMAP = 'imap';
export const CHAT_LIST_PROVIDER_MOVE_LABEL_ACTION_ADD_LOCAL = 'add-local';
export const CHAT_LIST_PROVIDER_MOVE_LABEL_ACTION_REPLACE_PROVIDER =
  'replace-provider';
export const CHAT_LIST_PROVIDER_MOVE_LOCAL_EFFECT_REMOVE = 'remove';
export const CHAT_LIST_BULK_EMAIL_DRAG_MIXED_INBOX_ERROR_KEY =
  'BULK_ACTION.EMAIL.DRAG_MIXED_INBOX_ERROR';
export const CHAT_LIST_EMAIL_DRAG_DROP_SOURCE = 'conversation-label-drop';

export const CHAT_LIST_MARK_AS_UNREAD_PENDING_TTL_MS = 12000;

export const resolveChatListAssignAgentContext = ({
  agent = null,
  conversationId = null,
  selectedConversations = [],
} = {}) => {
  const assigneeId = agent?.id ?? null;
  const assignee = assigneeId === null ? null : agent;
  const safeSelected = Array.isArray(selectedConversations)
    ? selectedConversations
    : [];
  const idArray = conversationId ? [conversationId] : safeSelected;
  return {
    assignee,
    assigneeId,
    idArray,
  };
};

export const buildChatListAssigneeSnapshots = (conversations = []) => {
  const safeConversations = Array.isArray(conversations) ? conversations : [];
  return safeConversations.filter(Boolean).map((chat) => ({
    chat,
    assignee: chat?.meta?.assignee || null,
    assigneeId: chat?.assignee_id,
  }));
};

export const resolveChatListPriorityAssignmentTargets = ({
  conversationId = null,
  selectedConversations = [],
} = {}) => {
  const safeSelected = Array.isArray(selectedConversations)
    ? selectedConversations
    : [];
  const ids =
    safeSelected.length > 0
      ? safeSelected
      : [conversationId].filter((id) => id !== null && id !== undefined);
  const isBulk = ids.length > 1;
  return {
    ids,
    isBulk,
    alertConversationLabel: isBulk
      ? `${ids.length} conversations`
      : conversationId,
  };
};

export const resolveChatListProviderMoveLabelAction = (provider = '') =>
  provider === CHAT_LIST_PROVIDER_GMAIL
    ? CHAT_LIST_PROVIDER_MOVE_LABEL_ACTION_ADD_LOCAL
    : CHAT_LIST_PROVIDER_MOVE_LABEL_ACTION_REPLACE_PROVIDER;

export const runChatListEmailProviderMoveLocalEffect = ({
  addConversationLocalLabel = null,
  conversationId = null,
  emitConversationMoveAndRemove = null,
  provider = '',
  replaceConversationProviderLabel = null,
  resolveLabelAction = resolveChatListProviderMoveLabelAction,
  shouldRemoveAfterSuccess = false,
  status = null,
  targetFolderTitle = null,
} = {}) => {
  if (shouldRemoveAfterSuccess) {
    const canEmitMove = typeof emitConversationMoveAndRemove === 'function';
    if (canEmitMove) {
      emitConversationMoveAndRemove(conversationId, status, {
        targetFolderTitle,
      });
    }
    return {
      action: CHAT_LIST_PROVIDER_MOVE_LOCAL_EFFECT_REMOVE,
      called: canEmitMove,
    };
  }

  const labelAction =
    typeof resolveLabelAction === 'function'
      ? resolveLabelAction(provider)
      : CHAT_LIST_PROVIDER_MOVE_LABEL_ACTION_REPLACE_PROVIDER;

  if (labelAction === CHAT_LIST_PROVIDER_MOVE_LABEL_ACTION_ADD_LOCAL) {
    const canAddLabel = typeof addConversationLocalLabel === 'function';
    if (canAddLabel) {
      addConversationLocalLabel(conversationId, targetFolderTitle);
    }
    return { action: labelAction, called: canAddLabel };
  }

  const canReplaceLabel =
    typeof replaceConversationProviderLabel === 'function';
  if (canReplaceLabel) {
    replaceConversationProviderLabel(
      conversationId,
      provider,
      targetFolderTitle
    );
  }
  return { action: labelAction, called: canReplaceLabel };
};

export const cloneChatListConversationSnapshot = (conversation = null) => {
  if (!conversation) {
    return null;
  }

  try {
    return JSON.parse(JSON.stringify(conversation));
  } catch (_) {
    return { ...conversation };
  }
};

export const buildChatListConversationSnapshots = ({
  conversationIds = [],
  getConversationById = null,
} = {}) => {
  if (
    !Array.isArray(conversationIds) ||
    typeof getConversationById !== 'function'
  ) {
    return [];
  }

  return conversationIds
    .map((conversationId) =>
      cloneChatListConversationSnapshot(getConversationById(conversationId))
    )
    .filter(Boolean);
};

export const runChatListOptimisticEmailProviderMove = ({
  addConversationLocalLabel = null,
  clearCacheForInbox = null,
  conversation = null,
  conversationId = null,
  emitConversationMoveAndRemove = null,
  inboxId = null,
  notifyConversationStatsChanged = null,
  provider = '',
  replaceConversationProviderLabel = null,
  shouldRemoveAfterSuccess = false,
  status = null,
  targetFolderTitle = null,
} = {}) => {
  const snapshot = cloneChatListConversationSnapshot(conversation);
  const effect = runChatListEmailProviderMoveLocalEffect({
    addConversationLocalLabel,
    conversationId,
    emitConversationMoveAndRemove,
    provider,
    replaceConversationProviderLabel,
    shouldRemoveAfterSuccess,
    status,
    targetFolderTitle,
  });

  if (inboxId && typeof clearCacheForInbox === 'function') {
    clearCacheForInbox(inboxId);
  }
  if (typeof notifyConversationStatsChanged === 'function') {
    notifyConversationStatsChanged();
  }

  return { effect, snapshot, status: 'applied' };
};

export const rollbackChatListOptimisticEmailProviderMove = ({
  clearCacheForInbox = null,
  inboxId = null,
  notifyConversationStatsChanged = null,
  restoreConversation = null,
  snapshot = null,
} = {}) => {
  if (!snapshot || typeof restoreConversation !== 'function') {
    return { restored: false, status: 'skipped' };
  }

  restoreConversation(snapshot);
  if (inboxId && typeof clearCacheForInbox === 'function') {
    clearCacheForInbox(inboxId);
  }
  if (typeof notifyConversationStatsChanged === 'function') {
    notifyConversationStatsChanged();
  }

  return { restored: true, status: 'restored' };
};

export const rollbackChatListOptimisticBulkEmailProviderMove = ({
  clearCacheForInbox = null,
  failedConversationIds = [],
  inboxId = null,
  notifyConversationStatsChanged = null,
  restoreConversation = null,
  snapshots = [],
} = {}) => {
  if (
    !Array.isArray(snapshots) ||
    !snapshots.length ||
    typeof restoreConversation !== 'function'
  ) {
    return { restoredCount: 0, status: 'skipped' };
  }

  const failedIds = Array.isArray(failedConversationIds)
    ? new Set(failedConversationIds)
    : new Set();
  const snapshotsToRestore = failedIds.size
    ? snapshots.filter((snapshot) => failedIds.has(snapshot?.id))
    : snapshots;

  snapshotsToRestore.forEach((snapshot) => restoreConversation(snapshot));

  if (
    snapshotsToRestore.length &&
    inboxId &&
    typeof clearCacheForInbox === 'function'
  ) {
    clearCacheForInbox(inboxId);
  }
  if (
    snapshotsToRestore.length &&
    typeof notifyConversationStatsChanged === 'function'
  ) {
    notifyConversationStatsChanged();
  }

  return {
    restoredCount: snapshotsToRestore.length,
    status: snapshotsToRestore.length ? 'restored' : 'skipped',
  };
};

export const resolveChatListBulkEmailDragDropRequestState = ({
  context = null,
  conversationId = null,
  conversationIds = [],
  dragDropSource = CHAT_LIST_EMAIL_DRAG_DROP_SOURCE,
  inboxId = null,
  provider = '',
  source = '',
} = {}) => {
  const ids = Array.isArray(conversationIds) ? conversationIds : [];
  const draggedConversationId =
    conversationId === null || conversationId === undefined
      ? null
      : String(conversationId);
  const isProviderDragDrop =
    source === dragDropSource &&
    Boolean(draggedConversationId) &&
    ids.length > 0 &&
    ids.some((id) => String(id) === draggedConversationId);

  if (!isProviderDragDrop) {
    return { status: 'single' };
  }

  const isSameProvider = context?.provider === provider;
  const isSameInbox = Number(context?.inboxId) === Number(inboxId);
  if (!context || !isSameProvider || !isSameInbox) {
    return { conversationIds: ids, status: 'invalid' };
  }

  return { context, conversationIds: ids, status: 'valid' };
};

export const runChatListBulkProviderMoveLocalEffect = ({
  applyProviderLabelUpdate = null,
  clearCacheForInbox = null,
  effect = null,
  emitCompleted = null,
  movedStatus = null,
  provider = '',
  targetFolderTitle = null,
} = {}) => {
  if (!effect) {
    return { status: 'skipped' };
  }

  const removeConversationIds = Array.isArray(effect.removeConversationIds)
    ? effect.removeConversationIds
    : [];
  const localUpdateConversationIds = Array.isArray(
    effect.localUpdateConversationIds
  )
    ? effect.localUpdateConversationIds
    : [];

  const shouldEmitCompleted =
    removeConversationIds.length && typeof emitCompleted === 'function';
  if (shouldEmitCompleted) {
    emitCompleted(removeConversationIds, movedStatus, {
      targetFolderId: effect.targetFolderId || null,
      targetFolderTitle: effect.targetFolderTitle || targetFolderTitle,
      targetLabelId: effect.targetLabelId || null,
      targetLabelTitle: effect.targetLabelTitle || null,
    });
  }

  const shouldApplyProviderLabelUpdate =
    localUpdateConversationIds.length &&
    typeof applyProviderLabelUpdate === 'function';
  if (shouldApplyProviderLabelUpdate) {
    localUpdateConversationIds.forEach((conversationId) => {
      applyProviderLabelUpdate({ conversationId, provider, targetFolderTitle });
    });
  }

  const cacheInboxId = effect.clearCachePayload?.inboxId;
  const shouldClearCache =
    Boolean(cacheInboxId) && typeof clearCacheForInbox === 'function';
  if (shouldClearCache) {
    clearCacheForInbox(cacheInboxId);
  }

  return {
    status: 'applied',
    cacheCleared: shouldClearCache,
    removedCount: shouldEmitCompleted ? removeConversationIds.length : 0,
    updatedCount: shouldApplyProviderLabelUpdate
      ? localUpdateConversationIds.length
      : 0,
  };
};

export const resolveChatListAssignAgentSuccessAlert = ({
  assignee = null,
  conversationId = null,
  translate = (key) => key,
} = {}) => {
  if (conversationId) {
    const agentName =
      assignee?.name || translate('CONVERSATION.HEADER.ASSIGNEE.UNASSIGNED');
    return translate(
      'CONVERSATION.CARD_CONTEXT_MENU.API.AGENT_ASSIGNMENT.SUCCESFUL',
      { agentName, conversationId }
    );
  }
  return translate('BULK_ACTION.ASSIGN_SUCCESFUL');
};

export const resolveChatListAssignLabelsSuccessAlert = ({
  conversationId = null,
  labelTitles = [],
  translate = (key) => key,
} = {}) => {
  if (conversationId) {
    const titles = Array.isArray(labelTitles) ? labelTitles : [];
    return translate(
      'CONVERSATION.CARD_CONTEXT_MENU.API.LABEL_ASSIGNMENT.SUCCESFUL',
      { labelName: titles.join(', '), conversationId }
    );
  }
  return translate('BULK_ACTION.LABELS.ASSIGN_SUCCESFUL');
};

export const normalizeChatListLabelChanges = (labels = null) => ({
  add: Array.isArray(labels?.add) ? labels.add : [],
  remove: Array.isArray(labels?.remove) ? labels.remove : [],
});

// Motivi con cui sync-channel segnala che thread/messaggio non esistono piu' nella casella.
const CHAT_LIST_PROVIDER_ITEM_GONE_REASONS = [
  'provider_thread_not_found',
  'provider_message_not_found',
];

export const resolveChatListToggleStarredErrorKey = (value, error) => {
  if (CHAT_LIST_PROVIDER_ITEM_GONE_REASONS.includes(error?.reason)) {
    return 'CONVERSATION.STARRED.PROVIDER_ITEM_GONE';
  }
  return value === true
    ? 'CONVERSATION.STARRED.ADD_FAILED'
    : 'CONVERSATION.STARRED.REMOVE_FAILED';
};

// sync-channel non sposta i messaggi in Posta inviata: se la conversazione Outlook
// e' fatta solo di quelli risponde 422 con questo motivo. Esito atteso, non un guasto.
const CHAT_LIST_SENT_ITEMS_NOT_MOVABLE_REASON = 'provider_message_not_movable';
export const CHAT_LIST_SENT_ITEMS_NOT_MOVABLE_ERROR_KEY =
  'CONVERSATION.MOVE.SENT_ITEMS_NOT_MOVABLE';
// L'email IMAP non e' piu' in nessuna cartella della casella (cancellata o
// spostata fuori da Otello): core risponde 422 con questo motivo, riprovare non serve.
const CHAT_LIST_MESSAGE_NOT_FOUND_REASON = 'message_not_found';
const CHAT_LIST_MESSAGE_NOT_IN_MAILBOX_ERROR_KEY =
  'CONVERSATION.MOVE.MESSAGE_NOT_IN_MAILBOX';

export const resolveChatListEmailFolderMoveErrorKey = (
  error,
  fallbackKey = CHAT_LIST_EMAIL_FOLDER_MOVE_ERROR_KEY
) => {
  const reason = error?.reason ?? error?.response?.data?.reason;
  if (reason === CHAT_LIST_MESSAGE_NOT_FOUND_REASON) {
    return CHAT_LIST_MESSAGE_NOT_IN_MAILBOX_ERROR_KEY;
  }
  return reason === CHAT_LIST_SENT_ITEMS_NOT_MOVABLE_REASON
    ? CHAT_LIST_SENT_ITEMS_NOT_MOVABLE_ERROR_KEY
    : fallbackKey;
};

export const resolveChatListBulkConversationIds = ({
  conversationId = null,
  selectedConversations = [],
} = {}) => {
  if (conversationId != null) {
    return [conversationId];
  }
  return Array.isArray(selectedConversations) ? [...selectedConversations] : [];
};

export const buildChatListBulkUpdateStatusPayload = ({
  conversationIds = [],
  status = null,
  snoozedUntil = null,
} = {}) => ({
  type: 'Conversation',
  ids: Array.isArray(conversationIds) ? conversationIds : [],
  fields: { status },
  snoozed_until: snoozedUntil,
});

export const scheduleChatListMarkUnreadPendingReset = ({
  id = null,
  pendingConversations = null,
  scheduleTimer = null,
  ttlMs = CHAT_LIST_MARK_AS_UNREAD_PENDING_TTL_MS,
} = {}) => {
  if (
    id == null ||
    id === '' ||
    !pendingConversations ||
    typeof pendingConversations.add !== 'function' ||
    typeof pendingConversations.delete !== 'function' ||
    typeof scheduleTimer !== 'function'
  ) {
    return null;
  }

  pendingConversations.add(id);
  return scheduleTimer(() => {
    pendingConversations.delete(id);
  }, ttlMs);
};

export const runChatListMarkAsUnread = async ({
  conversationId = null,
  errorKey = CHAT_LIST_MARK_AS_UNREAD_ERROR_KEY,
  markMessagesUnread = null,
  pendingConversations = null,
  schedulePendingReset = scheduleChatListMarkUnreadPendingReset,
  scheduleTimer = null,
  showError = null,
  showSuccess = null,
  successKey = CHAT_LIST_MARK_AS_UNREAD_SUCCESS_KEY,
} = {}) => {
  if (
    conversationId == null ||
    conversationId === '' ||
    !pendingConversations ||
    typeof pendingConversations.add !== 'function' ||
    typeof pendingConversations.delete !== 'function' ||
    typeof markMessagesUnread !== 'function'
  ) {
    return { error: null, status: 'invalid', timer: null };
  }

  try {
    pendingConversations.add(conversationId);

    await markMessagesUnread({ id: conversationId });

    if (typeof showSuccess === 'function') {
      showSuccess(successKey);
    }

    const timer =
      typeof schedulePendingReset === 'function'
        ? schedulePendingReset({
            id: conversationId,
            pendingConversations,
            scheduleTimer,
          })
        : null;

    return { error: null, status: 'marked-unread', timer };
  } catch (error) {
    pendingConversations.delete(conversationId);

    if (typeof showError === 'function') {
      showError(errorKey);
    }

    return { error, status: 'failed', timer: null };
  }
};

export const buildChatListAssignPriorityDispatchPayload = ({
  priority = null,
  conversationId = null,
} = {}) => ({
  priority,
  conversationId,
});

export const runChatListPriorityAssignment = async ({
  assignPriority = null,
  buildPayload = buildChatListAssignPriorityDispatchPayload,
  conversationId = null,
  priority = null,
  resolveTargets = resolveChatListPriorityAssignmentTargets,
  selectedConversations = [],
  showSuccess = null,
  successKey = CHAT_LIST_PRIORITY_CHANGE_SUCCESS_KEY,
} = {}) => {
  if (
    typeof assignPriority !== 'function' ||
    typeof buildPayload !== 'function' ||
    typeof resolveTargets !== 'function'
  ) {
    return { alertConversationLabel: null, ids: [], status: 'invalid' };
  }

  const { ids, alertConversationLabel } = resolveTargets({
    conversationId,
    selectedConversations,
  });

  await Promise.all(
    ids.map((id) =>
      assignPriority(
        buildPayload({
          priority,
          conversationId: id,
        })
      )
    )
  );

  if (typeof showSuccess === 'function') {
    showSuccess(successKey, {
      priority,
      conversationId: alertConversationLabel,
    });
  }

  return { alertConversationLabel, ids, status: 'assigned' };
};

export const buildChatListAssignAgentDispatchPayload = ({
  conversationId = null,
  selectedConversations = [],
  assigneeId = null,
} = {}) => ({
  type: 'Conversation',
  ids: conversationId || selectedConversations,
  fields: { assignee_id: assigneeId },
});

export const runChatListAssignAgent = async ({
  agent = null,
  applyAssigneeUpdate = null,
  buildDispatchPayload = buildChatListAssignAgentDispatchPayload,
  buildSnapshots = buildChatListAssigneeSnapshots,
  clearSelection = null,
  conversationId = null,
  errorKey = CHAT_LIST_ASSIGN_AGENT_ERROR_KEY,
  getConversationById = null,
  processBulkAction = null,
  resolveContext = resolveChatListAssignAgentContext,
  resolveSuccessAlert = resolveChatListAssignAgentSuccessAlert,
  selectedConversations = [],
  showError = null,
  showSuccess = null,
  translate = (key) => key,
} = {}) => {
  if (
    typeof applyAssigneeUpdate !== 'function' ||
    typeof buildDispatchPayload !== 'function' ||
    typeof buildSnapshots !== 'function' ||
    typeof getConversationById !== 'function' ||
    typeof processBulkAction !== 'function' ||
    typeof resolveContext !== 'function'
  ) {
    return { status: 'invalid' };
  }

  let assigneeSnapshots = [];

  try {
    const { assignee, assigneeId, idArray } = resolveContext({
      agent,
      conversationId,
      selectedConversations,
    });

    const conversations = idArray
      .map((id) => getConversationById(id))
      .filter(Boolean);

    assigneeSnapshots = buildSnapshots(conversations);

    conversations.forEach((chat) => {
      if (chat?.meta) {
        applyAssigneeUpdate(chat, assigneeId, assignee);
      }
    });

    await processBulkAction(
      buildDispatchPayload({
        conversationId,
        selectedConversations,
        assigneeId,
      })
    );

    if (typeof clearSelection === 'function') {
      clearSelection();
    }

    if (
      typeof showSuccess === 'function' &&
      typeof resolveSuccessAlert === 'function'
    ) {
      showSuccess(
        resolveSuccessAlert({
          assignee,
          conversationId,
          translate,
        })
      );
    }

    return { assigneeId, ids: idArray, status: 'assigned' };
  } catch (error) {
    assigneeSnapshots.forEach(({ chat, assignee, assigneeId }) => {
      if (!chat) return;
      applyAssigneeUpdate(chat, assigneeId, assignee);
    });

    if (typeof showError === 'function') {
      showError(errorKey);
    }

    return { error, status: 'failed' };
  }
};

export const buildChatListAssignLabelsDispatchPayload = ({
  conversationId = null,
  conversationIds = [],
  labelChanges = { add: [], remove: [] },
} = {}) => ({
  type: 'Conversation',
  ids: conversationId || conversationIds,
  labels: {
    add: Array.isArray(labelChanges?.add) ? labelChanges.add : [],
    remove: Array.isArray(labelChanges?.remove) ? labelChanges.remove : [],
  },
});

export const buildChatListBulkEmailBatchPayload = ({
  conversationIds = [],
  inboxId = null,
} = {}) => ({
  conversationIds: Array.isArray(conversationIds) ? conversationIds : [],
  inboxId,
});
